/**
 * @fileoverview Page-level error boundary for route-specific errors.
 * Allows the rest of the app to continue functioning when a page fails.
 */

import * as React from 'react';
import { Alert, Box, Container, Typography } from '@mui/material';
import { ArrowBack, ErrorOutlined, Refresh } from '@mui/icons-material';
import { useNavigate, useRouteError } from 'react-router';
import { Button } from '@/shared/components/Button';
import { errorDetailsSx, parseRouteError } from '../utils/errorHelpers';

export function PageErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();
  const { message, details } = parseRouteError(error, 'An error occurred while loading this page');

  return (
    <Container maxWidth='md' sx={{ py: 6 }}>
      <Alert
        severity='error'
        icon={<ErrorOutlined />}
        sx={{ '& .MuiAlert-icon': { fontSize: '2rem' } }}
      >
        <Typography variant='h6' gutterBottom>
          {message}
        </Typography>

        {details && process.env.NODE_ENV === 'development' && (
          <Box component='pre' sx={errorDetailsSx}>
            {details}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant='contained'
            size='small'
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
          <Button
            variant='outlined'
            size='small'
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </Box>
      </Alert>
    </Container>
  );
}

